import { telemetryIncrementAction } from 'core-events/actions';
import React, { JSX, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Search } from '@videoblocks/design-system/icons';

import { NavSearchContext, StatsigExperimentContext } from '../../context/NavSearchContext';
import ConversationalSearchTooltip from './ConversationalSearchTooltip';
import { getConversationalSearchUnavailableMessage } from './conversationalSearchMessaging';
import { SPARKLE_ICON_PATHS, SPARKLE_ICON_VIEWBOX } from './sparkleIcon';

/**
 * Vivid gradient used by the animated glow that traces the button outline
 * while AI search is available, to draw attention to the feature.
 */
const GLOW_PURPLE = '#9952e0';
const GLOW_PINK = '#ff66e6';
const GLOW_VIOLET = '#b357e1';

// The glow is a one-time attention-grabbing flourish: visible for this long
// after the button first appears, then unmounted so it doesn't keep animating.
// `GLOW_FADE_MS` is carved off the end of that window for a smooth fade-out,
// so the glow is already at opacity 0 by the time it unmounts.
const GLOW_VISIBLE_MS = 2000;
const GLOW_FADE_MS = 600;

const ICON_CLS = 'relative z-10 w-12 self-stretch flex justify-center items-center transition-opacity duration-300';
const HOVER_INTENT_DELAY_MS = 350;

export default function SearchWithAIToggle({
    disabledForContentType,
}: {
    disabledForContentType?: boolean;
}): JSX.Element {
    const { conversationalSearchEnabled, setConversationalSearchEnabled: setEnabled } = useContext(NavSearchContext);
    const { conversationalAnimatedToggleEnabled, conversationalSearchDiscoverability, showConversationalSearchAudio } =
        useContext(StatsigExperimentContext);
    // When set (> 0), hovering the toggle for this long auto-enables conversational search. 0 disables auto-on.
    const autoOnHoverDelayMs = conversationalSearchDiscoverability?.autoOnHoverDelayMs ?? 0;
    // Auto-enable-on-hover is opt-in via a positive delay (0 disables it).
    const autoOnEnabled = autoOnHoverDelayMs > 0;
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoOnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // While true, a progress trace draws around the button, reaching a complete
    // outline exactly when the auto-on hover delay elapses (see AutoOnHoverTrace).
    const [autoOnTracing, setAutoOnTracing] = useState(false);

    // Clear the auto-on timer on unmount so it can't fire setState after the button is gone.
    useEffect(() => {
        return () => {
            if (autoOnTimerRef.current !== null) {
                clearTimeout(autoOnTimerRef.current);
            }
        };
    }, []);

    // Visual state ignores stale context: when disabled for the current content type,
    // render as off regardless of whether the context has caught up yet.
    const effectivelyEnabled = conversationalSearchEnabled && !disabledForContentType;

    const toggleButtonStyles = [
        'relative h-14 p-1 rounded-full outline-2 -outline-offset-1 inline-flex items-center',
        'transition-colors duration-300 ease-in-out',
    ];

    if (disabledForContentType) {
        toggleButtonStyles.push('cursor-not-allowed outline-gray-500 bg-gray-200 outline-1');
    } else {
        toggleButtonStyles.push(
            'cursor-pointer outline-violet-200 bg-linear-to-r',
            effectivelyEnabled
                ? 'from-yellow-700 via-bubblegum-600 to-blueviolet-500'
                : 'from-yellow-50 via-fuchsia-100 to-violet-100',
        );
    }

    const toggleButton = (
        <button
            data-testid="conversational-search-toggle"
            type="button"
            aria-disabled={disabledForContentType}
            onClick={() => {
                if (!disabledForContentType) {
                    setEnabled((prev) => !prev);
                    void telemetryIncrementAction('conversational_search_toggle_click');
                }
            }}
            onMouseEnter={() => {
                if (disabledForContentType) return;
                hoverTimerRef.current = setTimeout(() => {
                    hoverTimerRef.current = null;
                    void telemetryIncrementAction('conversational_search_toggle_hover');
                }, HOVER_INTENT_DELAY_MS);

                // Auto-enable conversational search after a sustained hover, when the experiment
                // opts in (delay > 0) and the toggle is currently off. The progress trace draws
                // around the button over the same delay, completing its full outline as auto-on fires.
                if (autoOnEnabled && !conversationalSearchEnabled) {
                    setAutoOnTracing(true);
                    autoOnTimerRef.current = setTimeout(() => {
                        autoOnTimerRef.current = null;
                        setAutoOnTracing(false);
                        setEnabled(true);
                    }, autoOnHoverDelayMs);
                }
            }}
            onMouseLeave={() => {
                if (hoverTimerRef.current !== null) {
                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = null;
                }
                if (autoOnTimerRef.current !== null) {
                    clearTimeout(autoOnTimerRef.current);
                    autoOnTimerRef.current = null;
                }
                setAutoOnTracing(false);
            }}
            aria-pressed={effectivelyEnabled}
            aria-label={
                disabledForContentType
                    ? getConversationalSearchUnavailableMessage(showConversationalSearchAudio ?? false)
                    : effectivelyEnabled
                      ? 'Switch to standard search'
                      : 'Switch to AI search'
            }
            className={twMerge(...toggleButtonStyles)}
        >
            {/* Sliding white pill */}
            <div
                className={twMerge(
                    'absolute top-1 bottom-1 w-12 bg-white rounded-full shadow-lg transition-transform duration-300 ease-in-out',
                    effectivelyEnabled ? 'translate-x-12' : 'translate-x-0',
                )}
            />
            {/* Search icon */}
            <div className={twMerge(ICON_CLS, 'opacity-100', disabledForContentType && 'bg-gray-700 rounded-full')}>
                <Search
                    width={20}
                    height={20}
                    className={twMerge((disabledForContentType || effectivelyEnabled) && '[&_path]:fill-white')}
                />
            </div>
            {/* Sparkles icon (inline SVG for gradient fill) */}
            <div className={twMerge(ICON_CLS, effectivelyEnabled ? 'opacity-100' : 'opacity-50')}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox={SPARKLE_ICON_VIEWBOX}
                    width={24}
                    height={24}
                >
                    <defs>
                        <linearGradient id="sparkle-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop
                                offset="0%"
                                className="transition-all duration-300"
                                stopColor={effectivelyEnabled ? 'var(--color-blueviolet-600)' : 'var(--color-gray-950)'}
                            />
                            <stop
                                offset="100%"
                                className="transition-all duration-300"
                                stopColor={effectivelyEnabled ? 'var(--color-bubblegum-600)' : 'var(--color-gray-950)'}
                            />
                        </linearGradient>
                    </defs>
                    {SPARKLE_ICON_PATHS.map((d) => (
                        <path key={d} fill="url(#sparkle-gradient)" d={d} />
                    ))}
                </svg>
            </div>
        </button>
    );

    // The animated glow is purely decorative and would compete for attention
    // with the disabled (greyed-out) state, so it is only shown when usable.
    const trigger = (
        <span className="relative isolate ml-2 inline-flex items-center z-10">
            {/* The auto-on hover trace takes precedence: when it can play (delay > 0),
                the mount-time glow is suppressed so the two never compete. */}
            {!disabledForContentType && conversationalAnimatedToggleEnabled && !autoOnEnabled && <AnimatedAIGlow />}
            {!disabledForContentType && autoOnTracing && <AutoOnHoverTrace durationMs={autoOnHoverDelayMs} />}
            {toggleButton}
        </span>
    );

    return (
        <ConversationalSearchTooltip
            disabledForContentType={disabledForContentType}
            enabled={conversationalSearchEnabled}
            autoOnEnabled={autoOnEnabled}
            setEnabled={setEnabled}
            trigger={trigger}
        />
    );
}

/**
 * Decorative glow that traces the toggle's pill outline. A single thick, soft
 * stroke whose gradient is fixed in space (`userSpaceOnUse`) and fades to
 * transparent at both ends, so the color concentrates into blooms on the
 * sides and fades out at the top and bottom rather than forming an even ring.
 * A wide dash with one gap sweeps slowly around to make it shimmer.
 *
 * Rendered behind the button (`-z-10` within the parent's isolated stacking
 * context) so only the portion bleeding past the button's edges is visible.
 * Coordinates are kept in the reference design's 141×89 user space so the
 * fixed gradient lands exactly as designed; that canvas (with its centred
 * ~108×56 pill) is centred over the 104×56 button via negative insets.
 */
function AnimatedAIGlow(): React.ReactElement | null {
    // Show the glow for `GLOW_VISIBLE_MS` after mount, then unmount it so the
    // sweep animation doesn't keep running indefinitely.
    const [visible, setVisible] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setVisible(false), GLOW_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, []);

    if (!visible) return null;

    // Open, near-closed pill outline (a hairline gap at bottom-centre, capped
    // round) centred within the 141×89 canvas.
    const pillPath =
        'M83.364 72.5H44.5C29.036 72.5 16.5 59.964 16.5 44.5C16.5 29.036 29.036 16.5 44.5 16.5H96.5C111.964 16.5 124.5 29.036 124.5 44.5C124.5 59.964 111.964 72.5 96.5 72.5H83.8973';

    // some math so that the animation will be a seamless loop
    const dashPeriodPx = 0.9 * Math.sqrt((141 ** 2 + 89 ** 2) / 2) + 50;
    const loopOffsetPx = 6 * dashPeriodPx;
    const loopDurationS = loopOffsetPx / 100;

    return (
        <svg
            aria-hidden="true"
            width={141}
            height={89}
            viewBox="0 0 141 89"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            // Centre the 141×89 canvas over the 104×56 button: offset by half the
            // overflow on each axis ((141-104)/2 = 18.5, (89-56)/2 = 16.5).
            style={{ left: '-18.5px', top: '-16.5px' }}
            className="ai-glow-svg pointer-events-none absolute -z-10"
        >
            <style>{`
                @keyframes ai-glow-sweep {
                    from { stroke-dashoffset: 0; }
                    to { stroke-dashoffset: ${loopOffsetPx}px; }
                }
                @keyframes ai-glow-fadeout {
                    to { opacity: 0; }
                }
                .ai-glow-stroke {
                    stroke-dasharray: 90% 50;
                    animation: ai-glow-sweep ${loopDurationS}s linear infinite;
                }
                .ai-glow-svg {
                    animation: ai-glow-fadeout ${GLOW_FADE_MS}ms linear ${GLOW_VISIBLE_MS - GLOW_FADE_MS}ms forwards;
                }
                @media (prefers-reduced-motion: reduce) {
                    .ai-glow-stroke,
                    .ai-glow-svg { animation: none; }
                }
            `}</style>
            <defs>
                <linearGradient
                    id="ai-glow-gradient"
                    x1="235.242"
                    y1="-29.415"
                    x2="6.14331"
                    y2="5.56662"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={GLOW_PURPLE} stopOpacity={0} />
                    <stop offset={0.504808} stopColor={GLOW_PINK} />
                    <stop offset={0.817257} stopColor={GLOW_VIOLET} />
                    <stop offset={1} stopColor={GLOW_PINK} stopOpacity={0} />
                </linearGradient>
                <filter
                    id="ai-glow-blur"
                    x="0"
                    y="0"
                    width="141"
                    height="89"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feGaussianBlur stdDeviation="6" />
                </filter>
            </defs>
            <g filter="url(#ai-glow-blur)">
                <path
                    className="ai-glow-stroke"
                    d={pillPath}
                    stroke="url(#ai-glow-gradient)"
                    strokeWidth={9}
                    strokeLinecap="round"
                />
            </g>
        </svg>
    );
}

/**
 * Progress trace shown while the auto-on hover timer is counting down. Unlike
 * the decorative {@link AnimatedAIGlow} sweep, this draws the button's outline
 * on progressively from nothing to a complete ring, finishing exactly when the
 * `durationMs` (the auto-on hover delay) elapses — so the closed outline lands
 * at the same instant conversational search auto-enables.
 *
 * The path uses `pathLength={100}` so the draw is expressed in normalized
 * units regardless of the real geometry: a dasharray of one full length with
 * the dashoffset animating from full to zero reveals the stroke end-to-end.
 * Geometry and positioning mirror {@link AnimatedAIGlow} so the trace sits over
 * the same 104×56 button within the 141×89 canvas.
 */
function AutoOnHoverTrace({ durationMs }: { durationMs: number }): React.ReactElement {
    // Fully closed pill (no gap) so the completed trace surrounds the button.
    const pillPath = 'M44.5 16.5H96.5A28 28 0 0 1 96.5 72.5H44.5A28 28 0 0 1 44.5 16.5Z';

    return (
        <svg
            aria-hidden="true"
            width={141}
            height={89}
            viewBox="0 0 141 89"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            // Centre the 141×89 canvas over the 104×56 button, matching AnimatedAIGlow.
            style={{ left: '-18.5px', top: '-16.5px' }}
            className="ai-trace-svg pointer-events-none absolute -z-10"
        >
            <style>{`
                @keyframes ai-trace-draw {
                    from { stroke-dashoffset: 100; }
                    to { stroke-dashoffset: 0; }
                }
                .ai-trace-stroke {
                    stroke-dasharray: 100;
                    stroke-dashoffset: 100;
                    animation: ai-trace-draw ${durationMs}ms linear forwards;
                }
                @media (prefers-reduced-motion: reduce) {
                    .ai-trace-stroke { animation: none; stroke-dashoffset: 0; }
                }
            `}</style>
            <defs>
                <linearGradient
                    id="ai-trace-gradient"
                    x1="235.242"
                    y1="-29.415"
                    x2="6.14331"
                    y2="5.56662"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={GLOW_PURPLE} />
                    <stop offset={0.504808} stopColor={GLOW_PINK} />
                    <stop offset={0.817257} stopColor={GLOW_VIOLET} />
                    <stop offset={1} stopColor={GLOW_PINK} />
                </linearGradient>
                <filter
                    id="ai-trace-blur"
                    x="0"
                    y="0"
                    width="141"
                    height="89"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feGaussianBlur stdDeviation="3" />
                </filter>
            </defs>
            <g filter="url(#ai-trace-blur)">
                <path
                    className="ai-trace-stroke"
                    d={pillPath}
                    pathLength={100}
                    stroke="url(#ai-trace-gradient)"
                    strokeWidth={5}
                    strokeLinecap="round"
                />
            </g>
        </svg>
    );
}
